(() => {
  'use strict';

  /*
   * Kynalia × Shopify Variants × Appstle Subscriptions
   *
   * Prices and selling-plan IDs come from Shopify's variant JSON.
   * Prices display with £. No exchange-rate conversion is applied.
   */

  if (window.KynaliaVariantSubscriptionWidget) return;
  window.KynaliaVariantSubscriptionWidget = true;

  const CONFIG = {
    productPath: '/products/gland-support',
    variantPickerSelector:
      'variant-radios[data-url="/products/gland-support"]',
    appstleWidgetSelector:
      '.appstle_sub_widget[id^="appstle_subscription_widget"]',
    defaultSize: 'Under 11 kg',
    defaultPurchaseType: 'subscription',
    appstleRetryDelays: [0, 75, 175, 350, 650, 1000, 1600],

    sizes: {
      'Under 11 kg': {
        buttonLabel: 'Under 11 kg',
        tubs: 1,
        chews: 30
      },
      '11 - 34 kg': {
        buttonLabel: '11–34 kg',
        tubs: 2,
        chews: 60
      },
      'Over 34 kg': {
        buttonLabel: 'Over 34 kg',
        tubs: 3,
        chews: 90
      }
    }
  };

  const state = {
    selectedSize: CONFIG.defaultSize,
    purchaseType: CONFIG.defaultPurchaseType,
    variants: [],
    initialized: false,
    operationToken: 0,
    syncScheduled: false
  };

  function normalizePath(path) {
    return String(path || '').replace(/\/$/, '');
  }

  function isTargetProduct() {
    return normalizePath(window.location.pathname) ===
      normalizePath(CONFIG.productPath);
  }

  function getVariantPicker() {
    return document.querySelector(CONFIG.variantPickerSelector);
  }

  function getProductScope() {
    const picker = getVariantPicker();

    return picker?.closest('[id^="MainProduct-"]') ||
      picker?.closest('.product') ||
      picker?.parentElement ||
      document;
  }

  function getAppstleWidget() {
    const scope = getProductScope();

    return scope.querySelector(CONFIG.appstleWidgetSelector) ||
      document.querySelector(CONFIG.appstleWidgetSelector);
  }

  function parseVariants() {
    const picker = getVariantPicker();
    const json = picker?.querySelector('script[type="application/json"]');

    if (!json) return state.variants;

    try {
      const variants = JSON.parse(json.textContent);

      if (Array.isArray(variants) && variants.length) {
        state.variants = variants;
      }
    } catch (error) {
      console.warn(
        '[Kynalia widget] Could not parse Shopify variant data.',
        error
      );
    }

    return state.variants;
  }

  function getVariant(size = state.selectedSize) {
    return parseVariants().find((variant) =>
      variant.option1 === size ||
      variant.title === size ||
      variant.public_title === size
    ) || null;
  }

  function getSellingPlanAllocation(variant) {
    return variant?.selling_plan_allocations?.[0] || null;
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return '£' + amount.toFixed(2);
  }

  function formatPerChew(cents, chews) {
    return formatMoney(Math.round(Number(cents || 0) / chews));
  }

  function getPlanData() {
    const variant = getVariant();
    const allocation = getSellingPlanAllocation(variant);
    const meta = CONFIG.sizes[state.selectedSize];
    const oneTimePrice = Number(variant?.price || 0);
    const subscriptionPrice = Number(allocation?.price || oneTimePrice);
    const saving = Math.max(0, oneTimePrice - subscriptionPrice);
    const isSubscription = state.purchaseType === 'subscription';

    return {
      variant,
      allocation,
      meta,
      oneTimePrice,
      subscriptionPrice,
      saving,
      activePrice: isSubscription ? subscriptionPrice : oneTimePrice,
      isSubscription
    };
  }

  function setText(element, value) {
    if (element && element.textContent !== value) {
      element.textContent = value;
    }
  }

  function buildWidget() {
    if (document.querySelector('.kynalia-widget')) return;

    const picker = getVariantPicker();
    if (!picker) return;

    const widget = document.createElement('div');
    widget.className = 'kynalia-widget';
    widget.setAttribute('data-kynalia-widget', 'variant-subscription');

    const sizeButtons = Object.entries(CONFIG.sizes)
      .map(([nativeValue, size]) =>
        '<button' +
          ' class="kynalia-dog-size__button"' +
          ' type="button"' +
          ' data-kynalia-size="' + nativeValue + '"' +
          ' aria-pressed="false"' +
        '>' + size.buttonLabel + '</button>'
      )
      .join('');

    widget.innerHTML = `
      <section class="kynalia-dog-size" aria-labelledby="kynalia-dog-size-title">
        <div class="kynalia-dog-size__title" id="kynalia-dog-size-title">
          Choose Dog Size:
        </div>
        <div class="kynalia-dog-size__options" role="group" aria-label="Choose dog size">
    ` + sizeButtons + `
        </div>
      </section>

      <section class="kynalia-purchase" aria-labelledby="kynalia-purchase-title">
        <div class="kynalia-purchase__title" id="kynalia-purchase-title">
          Choose Your Plan:
        </div>

        <div class="kynalia-purchase__toggle" role="radiogroup" aria-label="Choose purchase option">
          <button
            class="kynalia-purchase__option kynalia-purchase__option--subscription"
            type="button"
            role="radio"
            aria-checked="false"
            data-kynalia-purchase="subscription"
          >
            <svg
              class="kynalia-purchase__subscription-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M5.1 16.05C4.73333 15.4167 4.45833 14.7667 4.275 14.1C4.09167 13.4333 4 12.75 4 12.05C4 9.81667 4.775 7.91667 6.325 6.35C7.875 4.78333 9.76667 4 12 4H12.175L10.575 2.4L11.975 1L15.975 5L11.975 9L10.575 7.6L12.175 6H12C10.3333 6 8.91667 6.5875 7.75 7.7625C6.58333 8.9375 6 10.3667 6 12.05C6 12.4833 6.05 12.9083 6.15 13.325C6.25 13.7417 6.4 14.15 6.6 14.55L5.1 16.05ZM12.025 23L8.025 19L12.025 15L13.425 16.4L11.825 18H12C13.6667 18 15.0833 17.4125 16.25 16.2375C17.4167 15.0625 18 13.6333 18 11.95C18 11.5167 17.95 11.0917 17.85 10.675C17.75 10.2583 17.6 9.85 17.4 9.45L18.9 7.95C19.2667 8.58333 19.5417 9.23333 19.725 9.9C19.9083 10.5667 20 11.25 20 11.95C20 14.1833 19.225 16.0833 17.675 17.65C16.125 19.2167 14.2333 20 12 20H11.825L13.425 21.6L12.025 23Z"
                fill="currentColor"
              ></path>
            </svg>
            <span>Subscribe &amp; Save</span>
          </button>

          <button
            class="kynalia-purchase__option kynalia-purchase__option--one-time"
            type="button"
            role="radio"
            aria-checked="false"
            data-kynalia-purchase="one-time"
          >
            <span>One-Time Purchase</span>
          </button>
        </div>

        <p class="kynalia-purchase__message" aria-live="polite">
          <span class="kynalia-purchase__message-prefix"></span>
          <strong class="kynalia-purchase__message-highlight"></strong>
          <span class="kynalia-purchase__message-suffix"></span>
        </p>
      </section>

      <section class="kynalia-plan-card" aria-live="polite">
        <div class="kynalia-plan-card__summary">
          <span class="kynalia-plan-card__radio" aria-hidden="true">
            <span></span>
          </span>

          <div class="kynalia-plan-card__information">
            <div class="kynalia-plan-card__heading">
              <span class="kynalia-plan-card__title"></span>
              <span class="kynalia-plan-card__badge"></span>
            </div>
            <div class="kynalia-plan-card__subtitle"></div>
          </div>

          <div class="kynalia-plan-card__price-information">
            <div class="kynalia-plan-card__price"></div>
            <div class="kynalia-plan-card__compare-price"></div>
          </div>
        </div>

        <div class="kynalia-subscription-benefits">
          <div class="kynalia-subscription-benefits__item">
            <span class="kynalia-subscription-benefits__check" aria-hidden="true">✓</span>
            <span>Free Shipping</span>
          </div>
          <div class="kynalia-subscription-benefits__item">
            <span class="kynalia-subscription-benefits__check" aria-hidden="true">✓</span>
            <span>30-Day Money-Back Guarantee</span>
          </div>
          <div class="kynalia-subscription-benefits__item">
            <span class="kynalia-subscription-benefits__check" aria-hidden="true">✓</span>
            <span>Pause or Cancel Anytime</span>
          </div>
        </div>
      </section>
    `;

    picker.insertAdjacentElement('beforebegin', widget);
  }

  function visuallyHideNativeControls() {
    const picker = getVariantPicker();
    const appstle = getAppstleWidget();

    if (picker) picker.classList.add('kynalia-native-control');
    if (appstle) appstle.classList.add('kynalia-native-control');
  }

  function renderWidget() {
    const widget = document.querySelector('.kynalia-widget');
    if (!widget) return;

    const data = getPlanData();
    if (!data.variant || !data.meta) return;

    widget.classList.toggle('kynalia-is-subscription', data.isSubscription);
    widget.classList.toggle('kynalia-is-one-time', !data.isSubscription);

    widget.querySelectorAll('.kynalia-dog-size__button').forEach((button) => {
      const isActive = button.dataset.kynaliaSize === state.selectedSize;
      const nativeInput = getVariantPicker()?.querySelector(
        'input[name="Dog Size"][value="' +
        CSS.escape(button.dataset.kynaliaSize) +
        '"]'
      );

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
      button.disabled = Boolean(nativeInput?.disabled);
    });

    widget.querySelectorAll('.kynalia-purchase__option').forEach((button) => {
      const isActive = button.dataset.kynaliaPurchase === state.purchaseType;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-checked', String(isActive));
    });

    const prefix = widget.querySelector('.kynalia-purchase__message-prefix');
    const highlight = widget.querySelector('.kynalia-purchase__message-highlight');
    const suffix = widget.querySelector('.kynalia-purchase__message-suffix');

    if (data.isSubscription) {
      setText(prefix, 'Keep the scooting away with ');
      setText(highlight, 'consistent daily support');
      setText(suffix, '');
    } else {
      setText(prefix, 'Switch to a subscription to ');
      setText(highlight, 'save an extra ' + formatMoney(data.saving));
      setText(suffix, ' + free shipping');
    }

    setText(
      widget.querySelector('.kynalia-plan-card__title'),
      data.meta.tubs + ' ' + (data.meta.tubs === 1 ? 'Tub' : 'Tubs')
    );

    const badge = widget.querySelector('.kynalia-plan-card__badge');

    setText(
      badge,
      data.isSubscription ? 'Save ' + formatMoney(data.saving) : ''
    );

    badge.hidden = !data.isSubscription || data.saving <= 0;

    setText(
      widget.querySelector('.kynalia-plan-card__subtitle'),
      data.meta.chews +
      ' Chews · 30-Day Supply · ' +
      formatPerChew(data.activePrice, data.meta.chews) +
      '/chew'
    );

    setText(
      widget.querySelector('.kynalia-plan-card__price'),
      formatMoney(data.activePrice)
    );

    const comparePrice = widget.querySelector(
      '.kynalia-plan-card__compare-price'
    );

    setText(
      comparePrice,
      data.isSubscription ? formatMoney(data.oneTimePrice) : ''
    );

    comparePrice.hidden = !data.isSubscription;
  }

  function getNativeVariantInput(size) {
    return getVariantPicker()?.querySelector(
      'input[name="Dog Size"][value="' + CSS.escape(size) + '"]'
    ) || null;
  }

  function dispatchNativeSelection(input) {
    if (!input) return;

    input.click();

    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function applyNativePurchaseSelection(token) {
    if (token !== state.operationToken) return false;

    const appstle = getAppstleWidget();
    const variant = getVariant();
    const allocation = getSellingPlanAllocation(variant);

    if (!appstle || !variant) return false;

    let input;

    if (state.purchaseType === 'subscription') {
      if (!allocation?.selling_plan_id) return false;

      input = appstle.querySelector(
        'input[name="selling_plan"][value="' +
        allocation.selling_plan_id +
        '"]'
      );
    } else {
      input = [...appstle.querySelectorAll('input[name="selling_plan"]')]
        .find((candidate) => candidate.value === '');
    }

    if (!input) return false;

    if (!input.checked) dispatchNativeSelection(input);

    return input.checked;
  }

  function queuePurchaseSelection(token = state.operationToken) {
    CONFIG.appstleRetryDelays.forEach((delay) => {
      window.setTimeout(() => {
        if (token !== state.operationToken) return;

        visuallyHideNativeControls();
        applyNativePurchaseSelection(token);
      }, delay);
    });
  }

  function selectSize(size) {
    if (!CONFIG.sizes[size]) return;

    state.selectedSize = size;
    const token = ++state.operationToken;

    renderWidget();

    const nativeInput = getNativeVariantInput(size);

    if (nativeInput && !nativeInput.checked) {
      dispatchNativeSelection(nativeInput);
    }

    queuePurchaseSelection(token);
  }

  function selectPurchaseType(purchaseType) {
    if (!['subscription', 'one-time'].includes(purchaseType)) return;

    state.purchaseType = purchaseType;
    const token = ++state.operationToken;

    renderWidget();
    queuePurchaseSelection(token);
  }

  function bindEvents() {
    if (
      document.documentElement.dataset.kynaliaWidgetEventsBound === 'true'
    ) {
      return;
    }

    document.documentElement.dataset.kynaliaWidgetEventsBound = 'true';

    document.addEventListener('click', (event) => {
      const sizeButton = event.target.closest('.kynalia-dog-size__button');

      if (sizeButton) {
        selectSize(sizeButton.dataset.kynaliaSize);
        return;
      }

      const purchaseButton = event.target.closest(
        '.kynalia-purchase__option'
      );

      if (purchaseButton) {
        selectPurchaseType(purchaseButton.dataset.kynaliaPurchase);
      }
    });

    document.addEventListener('change', (event) => {
      const input = event.target;

      if (
        input.matches?.(
          CONFIG.variantPickerSelector + ' input[name="Dog Size"]'
        ) &&
        input.checked &&
        CONFIG.sizes[input.value]
      ) {
        state.selectedSize = input.value;
        renderWidget();
      }
    });
  }

  function sync() {
    if (!isTargetProduct()) return;

    const picker = getVariantPicker();
    if (!picker) return;

    parseVariants();
    buildWidget();
    visuallyHideNativeControls();
    bindEvents();
    renderWidget();

    if (!state.initialized) {
      state.initialized = true;
      selectSize(CONFIG.defaultSize);
    } else {
      queuePurchaseSelection(state.operationToken);
    }
  }

  function scheduleSync() {
    if (state.syncScheduled) return;

    state.syncScheduled = true;

    window.requestAnimationFrame(() => {
      state.syncScheduled = false;
      sync();
    });
  }

  const observer = new MutationObserver((mutations) => {
    const relevantMutation = mutations.some((mutation) => {
      const changedInsideAppstle = mutation.target.closest?.(
        CONFIG.appstleWidgetSelector
      );

      if (changedInsideAppstle) return true;

      return [...mutation.addedNodes].some((node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (
          node.matches?.(CONFIG.variantPickerSelector) ||
          node.matches?.(CONFIG.appstleWidgetSelector) ||
          node.querySelector?.(CONFIG.variantPickerSelector) ||
          node.querySelector?.(CONFIG.appstleWidgetSelector)
        )
      );
    });

    if (relevantMutation) scheduleSync();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, {
      once: true
    });
  } else {
    scheduleSync();
  }
})();